import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useReducer,
} from "react";
import { useSearchParams } from "react-router-dom";

const { REACT_APP_CDN_URL = "" } = process.env;

export const TRANSLATION_KEY = {
  game_name: "game_name",
  start_date: "start_date",
  end_date: "end_date",
  normal_spin_id: "normal_spin_id",
  free_spin_id: "free_spin_id",
  spin_count: "spin_count",
  bet_size: "bet_size",
  symbol: "symbol",
  rate: "rate",
  bonus_count: "bonus_count",
  explanation: "explanation",
  calculation: "calculation",
  formula: "formula",
  spin: "spin",
  bonus: "bonus",
  end: "end",
  on_going: "on_going",
  buy_free_spin_amount: "buy_free_spin_amount",
  count: "count",
  free_play: "free_play",
  line: "line",
  way: "way",
  win: "win",
  bet: "bet",
  payout: "payout",
  multiplier: "multiplier",
  details_header: "details_header",
  details_credit: "details_credit",
  roundReferenceNumber: "roundReferenceNumber",
  profit: "profit",
  result: "result",
  no_of_balls: "no_of_balls",
  bonus_balls: "bonus_balls",
  pockets: "pockets",
  bingo: "bingo",
  winning_ball_no: "winning_ball_no",
  no: "no",
  grand_total: "grand_total",
  with_bonus_balls: "with_bonus_balls",
  selected: "selected",
} as const;

type Translator = {
  get(key: keyof typeof TRANSLATION_KEY): string;
  getGameName(gameCode: string): string;
};

const TranslationContext = createContext<Record<string, string>>({});
const TranslationContextDispatch = createContext<
  (newValue: Record<string, string>) => void
>(() => {});

export const TranslationProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [translation, dispatch] = useReducer(translationReducer, {});

  return (
    <TranslationContext value={translation}>
      <TranslationContextDispatch value={dispatch}>
        {children}
      </TranslationContextDispatch>
    </TranslationContext>
  );
};

export const useTranslator = (): Translator => {
  const translation = useContext(TranslationContext);
  return {
    get: (key: keyof typeof TRANSLATION_KEY) => translation[key] || key,
    getGameName: (gameCode: string) => translation[gameCode] || gameCode,
  };
};
export const useTranslationReducer = () => {
  const dispatch = useContext(TranslationContextDispatch);
  const [searchParam] = useSearchParams();

  return async (lang: string | undefined = undefined) => {
    const l = searchParam.get("lang") || lang || "en";
    document.documentElement.lang = l;
    const gameNameUri = new URL(
      `slot/localizations/u13/${l}/game-name.json`,
      REACT_APP_CDN_URL
    );
    const betHistoryUri = new URL(
      `slot/localizations/u13/${l}/bet-history.json`,
      REACT_APP_CDN_URL
    );
    return Promise.all([
      fetch(gameNameUri)
        .catch((error) =>
          fetch(
            new URL(
              `slot/localizations/u13/en/game-name.json`,
              REACT_APP_CDN_URL
            )
          )
        )
        .then(
          async (response) => (await response.json()) as Record<string, string>
        ),
      fetch(betHistoryUri)
        .catch((error) =>
          fetch(
            new URL(
              `slot/localizations/u13/en/bet-history.json`,
              REACT_APP_CDN_URL
            )
          )
        )
        .then(
          async (response) => (await response.json()) as Record<string, string>
        ),
    ]).then((results) => {
      dispatch(
        results.reduce((acc, result) => {
          return { ...acc, ...result };
        }, {})
      );
    });
  };
};

const translationReducer = (
  previousValue: Record<string, string>,
  newValue: Record<string, string>
) => ({ ...previousValue, ...newValue });
